// /w/:wid/reports — the inbox (Classic Frame, Oct 10 2026): the title bar, ATTACK · SUPPORT · MARKET (every scouting
// report under Attack; game/reports.ts inboxTabOf), the day's numbers under Attack, reports grouped by day with their wax
// seals in full-width rows; unread rows carry the gold edge. The tab lives in ?t= (old ?f= links still land), else the
// one last used on this device. Tap a row for the report; the gear opens mark-read / delete for this tab or all tabs.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { WorldPage, useWorldCity } from "@/app/WorldPage";
import { art } from "@/kit/art";
import { ScreenHero } from "@/kit/city";
import { Confirm, Sheet } from "@/kit/overlays";
import { Button, Card, CardHead, Empty, Medallion, Row, RowText, Seg, Skeleton, cx } from "@/kit/primitives";
import { useNow } from "@/state/clock";
import { usePrompts } from "@/state/prompts";
import { useReportMutations, useReports } from "@/state/socialQueries";
import { fmtNum, plural } from "@/game/format";
import { fmtClock } from "@/game/time";
import { INBOX_TABS, dayGroups, inTab, inboxItems, inboxStats, inboxTabParam, type InboxTab, type InboxItem } from "@/game/reports";
import "../social.css";

const TAB_KEY = "valor-web:reports-tab";
/** the tab last used on this device (Attack the first time) */
export const lastReportsTab = (): InboxTab => {
  try {
    return inboxTabParam(localStorage.getItem(TAB_KEY)) ?? "attack";
  } catch {
    return "attack";
  }
};
const rememberTab = (t: InboxTab) => {
  try {
    localStorage.setItem(TAB_KEY, t);
  } catch {
    /* private mode: just this visit */
  }
};

export function ReportsInbox() {
  return (
    <WorldPage>
      <Inbox />
    </WorldPage>
  );
}

export function Inbox() {
  const { wid, base } = useWorldCity();
  const { id: openId } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab: InboxTab = inboxTabParam(params.get("t"), params.get("f")) ?? lastReportsTab();
  const setTab = (t: InboxTab) => { rememberTab(t); setParams({ t }, { replace: true }); };
  useEffect(() => { rememberTab(tab); }, [tab]);
  const q = useReports(wid);
  const { markRead, markAllRead, remove, clearFlag } = useReportMutations(wid);
  const now = useNow(30_000);
  const [gear, setGear] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<"read" | "all" | null>(null);
  const [scope, setScope] = useState<"tab" | "all">("tab");

  const all = useMemo(() => inboxItems(q.data), [q.data]);
  const items = useMemo(() => all.filter((it) => inTab(it.item.b, tab)), [all, tab]);
  const groups = useMemo(() => dayGroups(items, now), [items, now]);
  const stats = useMemo(() => inboxStats(items, now), [items, now]);
  const unreadBy = (t: InboxTab) => all.filter((it) => it.unread && inTab(it.item.b, t)).length;
  const tabLabel = INBOX_TABS.find((t) => t.value === tab)!.label;
  // the gear acts on this tab or on every tab
  const scoped = scope === "tab" ? items : all;
  const scopedUnread = scoped.filter((it) => it.unread).length;

  // the list is the "seen" signal for village/get's new_report flag
  useEffect(() => {
    if (q.data) clearFlag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.dataUpdatedAt]);

  const open = (it: InboxItem) => {
    if (it.unread) markRead.mutate(it.id);
    nav(`${base}/reports/${it.id}?t=${tab}`);
  };
  const markGroup = (list: InboxItem[]) => {
    const ids = list.filter((it) => it.unread).map((it) => it.id);
    if (ids.length) markAllRead.mutate(ids);
  };
  const doDelete = async (which: "read" | "all") => {
    const ids = scoped.filter((it) => which === "all" || !it.unread).map((it) => it.id);
    setConfirmDelete(null);
    setGear(false);
    if (!ids.length) return usePrompts.getState().toast("Nothing to delete.");
    try {
      await remove.mutateAsync(ids);
      usePrompts.getState().toast(`${plural(ids.length, "report")} deleted.`, "success");
    } catch (e) {
      usePrompts.getState().error(e);
    }
  };

  return (
    <div className="stack classic">
      <ScreenHero title="Reports" onBack={() => nav(base)} onGear={() => setGear(true)} gearLabel="Inbox options" />
      <Seg value={tab} onChange={setTab} options={INBOX_TABS.map((t) => ({ value: t.value, label: t.label, badge: unreadBy(t.value) || undefined }))} />
      {tab === "attack" && q.data && (
        <div className="rp-sum" aria-label="Today">
          <span>Today · <b>{plural(stats.today, "report")}</b></span>
          <span>Hauled <b className={stats.hauled ? "green" : undefined}>{fmtNum(stats.hauled)}</b> · Lost <b className={stats.lost ? "crim" : undefined}>{fmtNum(stats.lost)}</b></span>
        </div>
      )}

      {q.isLoading && !q.data && <div className="stack"><Skeleton h={260} /><Skeleton h={200} /></div>}
      {q.isError && !q.data && <Card><Empty>Couldn't read your reports. <Button size="sm" onClick={() => void q.refetch()}>Try again</Button></Empty></Card>}
      {q.data && !items.length && (
        <Card>
          <Empty>{tab === "attack" ? "No attack reports — your battles, defenses and spies will report here." : tab === "support" ? "No support reports — troops you send or receive will report here." : "No market reports — your trades and shipments will report here."}</Empty>
        </Card>
      )}

      {groups.map((g) => {
        const unread = g.items.filter((it) => it.unread).length;
        return (
          <Card key={g.label} className="rp-group">
            <CardHead
              title={g.label}
              aside={unread ? (
                <span>{plural(unread, "unread", "unread")} · <button type="button" className="link" style={{ font: "inherit" }} onClick={() => markGroup(g.items)}>Mark all read</button></span>
              ) : plural(g.items.length, "report")}
            />
            <div className="rows">
              {g.items.map((it) => <ReportRow key={it.id} it={it} open={it.id === openId} onOpen={() => open(it)} />)}
            </div>
          </Card>
        );
      })}
      {q.data && all.length >= 100 && <p className="c-note">The game keeps the latest hundred reports.</p>}

      <Sheet open={gear} onClose={() => setGear(false)} label="Inbox options" title={<h3 className="eyebrow" style={{ margin: "0 0 14px" }}>Inbox</h3>}>
        <div className="stack" style={{ gap: 10 }}>
          <Seg value={scope} onChange={setScope} options={[{ value: "tab", label: tabLabel }, { value: "all", label: "All tabs" }]} />
          <Button kind="steel" size="md" wide disabled={!scopedUnread} onClick={() => { markGroup(scoped); setGear(false); }}>Mark {scope === "tab" ? tabLabel.toLowerCase() : "everything"} read{scopedUnread ? ` (${scopedUnread})` : ""}</Button>
          <Button kind="steel" size="md" wide disabled={!scoped.some((it) => !it.unread)} onClick={() => setConfirmDelete("read")}>Delete the read {scope === "tab" ? `${tabLabel.toLowerCase()} ` : ""}reports</Button>
          <Button kind="crimson" size="md" wide disabled={!scoped.length} onClick={() => setConfirmDelete("all")}>Delete every {scope === "tab" ? `${tabLabel.toLowerCase()} ` : ""}report</Button>
          <p className="c-note">Deleted reports can't be brought back. The game only keeps the latest hundred anyway.</p>
        </div>
      </Sheet>
      <Confirm
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title={confirmDelete === "all" ? `Delete every ${scope === "tab" ? `${tabLabel.toLowerCase()} ` : ""}report?` : `Delete the read ${scope === "tab" ? `${tabLabel.toLowerCase()} ` : ""}reports?`}
        text={confirmDelete === "all" ? `All ${plural(scoped.length, "report")} will be gone for good.` : `${plural(scoped.filter((it) => !it.unread).length, "report")} you have already read will be gone for good. Unread ones stay.`}
        action="Delete"
        onConfirm={() => void doDelete(confirmDelete!)}
        busy={remove.isPending}
      />
    </div>
  );
}

/** One inbox row: seal · subject (names in gold) · the line under · time and the number on the right. */
export function ReportRow({ it, open, onOpen }: { it: InboxItem; /** the report open beside the list (split panes) */ open?: boolean; onOpen: () => void }) {
  const v = it.value;
  return (
    <Row as="button" unread={it.unread} className={cx("rp-row", it.unread && "is-unread", open && "open")} aria-current={open || undefined} onClick={onOpen} aria-label={`${it.subject}, ${it.unread ? "unread" : "read"}`}>
      <Medallion src={art.social.seal(it.seal)} alt="" size={64} className="rp-seal" />
      <RowText line={<Subject text={it.subject} />} meta={it.line || undefined} />
      <div className="right rp-right">
        <div className="rp-time">{it.unread && <i className="rp-dot" aria-hidden="true" />}{Number.isFinite(it.time) ? fmtClock(it.time) : "—"}</div>
        {v && <div className={cx("rp-val", v.tone)}>{v.label ? <span className="lbl">{v.label} </span> : null}<b>{v.value}</b></div>}
      </div>
    </Row>
  );
}

/** "Quark - 001 attacks Gorakzurkarr" with the other party in gold; the trailing coordinates dropped (the line under
 *  shows them). Subjects the game writes: "<A> attacks|conquers|supports <B> (x,y)", "Your support in <A> was attacked
 *  by <B>", "Trade Complete Between <A> and <B>", "Offer of <A> accepted", "<A> has accepted your offer",
 *  "Resources Sent To <A>", "Resources Received From <A>", "Instant Spy on <A> (x,y)", "Incoming Rally on <A> (x,y)". */
export function Subject({ text }: { text: string }) {
  const t = text.replace(/\s*\(\s*-?\d+\s*,\s*-?\d+\s*\)\s*$/, "");
  const two = /^(.*?)\s+(attacks|conquers|supports|was attacked by|and)\s+(.*)$/i.exec(t);
  if (two) return <>{two[1]} {two[2]} <b>{two[3]}</b></>;
  const pre = /^(Offer of|Instant Spy on|Incoming Rally on|Resources Sent To|Resources Received From)\s+(.*?)(\s+accepted)?$/i.exec(t);
  if (pre) return <>{pre[1]} <b>{pre[2]}</b>{pre[3] ?? ""}</>;
  const post = /^(.*?)\s+(has accepted your offer)$/i.exec(t);
  if (post) return <><b>{post[1]}</b> {post[2]}</>;
  return <>{t}</>;
}
